import { Button } from "@/components/ui/button";
import { EVOHR_LOGO } from "@/lib/logo";
import { ArrowRight, Play, Layers, Globe, Sparkles, Shield, Server } from "lucide-react";
import { useDeferredTranslation } from "@/hooks/useDeferredTranslation";
import { lazy, Suspense } from "react";

/* ─── Stats bar items ─── */
const stats = [
  { icon: Layers, value: "22+", label: "Modules" },
  { icon: Globe, value: "6", label: "Industries" },
  { icon: Sparkles, value: "AI-Assisted", label: "Workflows" },
  { icon: Shield, value: "Enterprise", label: "Security" },
  { icon: Server, value: "Multi-Tenant", label: "Architecture" },
];

// Heavy command center visual — lazy loaded, desktop only
const HeroVisual = lazy(() => import("./HeroVisual"));

export function PremiumHero({
  onRequestDemo, onExplore }: { onRequestDemo: () => void; onExplore: () => void }) {
    const { t } = useDeferredTranslation();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-[72px]">
      {/* Background layers — hidden on mobile for faster paint */}
      <div className="hidden md:block absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,hsl(217_60%_15%/0.5),transparent_70%)] dark:opacity-100 opacity-30" />
      <div className="hidden md:block absolute inset-0 bg-[radial-gradient(circle_at_80%_60%,hsl(42_80%_50%/0.04),transparent_50%)]" />
      {/* Animated glow — desktop only */}
      <div className="hidden lg:block absolute top-[40%] right-[25%] w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,hsl(42_80%_50%/0.06),transparent_60%)] animate-breathe pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-8 w-full grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-4 items-center py-12 lg:py-0">
        {/* ─── LEFT: Copy ─── */}
        <div className="space-y-7 max-w-xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/25 bg-gold/5">
            <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
            <span className="text-[11px] font-semibold tracking-widest text-gold uppercase">
              {t("landing.premium.badge", "AI-POWERED ENTERPRISE OS")}
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-[2.75rem] lg:text-[3.5rem] xl:text-[4rem] font-bold tracking-tight leading-[1.08]">
            <span className="text-foreground">{t("landing.premium.heroTitle1", "EVOHR - The Enterprise")}</span>
            <br />
            <span className="text-foreground">{t("landing.premium.heroTitle2", "Operating System,")}</span>
            <br />
            <span className="bg-gradient-to-r from-gold via-gold to-gold/70 bg-clip-text text-transparent italic">
              {t("landing.premium.heroTitle3", "Powered by AI")}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-base lg:text-lg text-muted-foreground leading-relaxed max-w-lg">
            {t("landing.premium.heroSubtitle", "EvoHR helps companies manage HR, payroll, attendance, recruitment, performance, finance, and operations in one unified AI-powered platform.")}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Button
              size="lg"
              onClick={onRequestDemo}
              className="h-13 px-7 text-sm font-semibold bg-gold hover:bg-gold/90 text-gold-foreground rounded-lg border border-gold/40 shadow-[0_0_30px_hsl(42_80%_50%/0.25)] transition-all duration-500 hover:shadow-[0_0_50px_hsl(42_80%_50%/0.35)]"
            >
              {t("landing.premium.requestDemo", "Request an Executive Demo")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={onExplore}
              className="h-13 px-7 text-sm font-semibold border-border/30 hover:border-foreground/20 hover:bg-foreground/5 rounded-lg"
            >
              <Play className="mr-2 h-3.5 w-3.5" />
              {t("landing.premium.explorePlatform", "Explore the Ecosystem")}
            </Button>
          </div>

          {/* Stats strip — show only first 3 on mobile */}
          <div className="flex flex-wrap gap-2 pt-4">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg bg-card/40 border border-border/10${i >= 3 ? " hidden sm:flex" : ""}`}
              >
                <s.icon className="h-3.5 w-3.5 text-gold" />
                <span className="text-xs font-bold text-foreground">{s.value}</span>
                <span className="text-[10px] text-muted-foreground">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ─── RIGHT: Command Center Visual — lazy loaded, desktop only ─── */}
        <div className="relative hidden lg:block">
          <Suspense fallback={<div className="w-full aspect-square max-w-[620px] mx-auto" />}>
            <HeroVisual />
          </Suspense>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
}
